import { getPrescriptions } from 'feature/claims/claimSlice';
import useDialogConfirm from 'hooks/DialogConfirm.hook';
import { ChangeEvent, FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/store';
import { Box, Button, Stack, StackRadio, Typography } from 'styles';
import {
	ALL_LENS_BENEFITS,
	IClaim,
	IClaimComponents,
	optionsExam,
	optionsMaterials,
	optionsSpecificLens,
} from 'types/Claim';
import { NO_MAT_NO_EXAM } from 'types/dialogtypes';
import { IExam, IMaterials } from 'types/Member';
import { PageProps } from 'types/newClaimPageType';
import { useTranslation } from 'utils/translations';

type Options = {
	label: string;
	value: number;
	enableIf: number[];
	statisticValue?: number;
};

const Page0: FC<PageProps> = props => {
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: 'clientLabels',
	});
	const dispatch = useDispatch<AppDispatch>();
	const { formData, setFormData, submitPage } = props;
	const { member } = useSelector((store: RootState) => store.member);
	const lensTypeRequired = [4, 1, 20];
	const isLensTypeRequired = lensTypeRequired.includes(formData.materials);
	const lensBenefits = member
		? member.benefit.materials.filter(el =>
				ALL_LENS_BENEFITS.includes(el.serviceId),
			)
		: [];

	const getOptions = (eligible: IExam[] | IMaterials[], options: Options[]) =>
		options
			.filter(opt => {
				return opt.enableIf.find(optEnable => {
					return eligible.find(
						el => el.memberBenefitAmount !== null && el.serviceId === optEnable,
					);
				});
			})
			.map(el => ({ ...el, label: tClientLabels(el.label) }));

	let benefits = [
		...(member?.benefit ? member.benefit.exam : []),
		...(member?.benefit ? member.benefit.materials : []),
	];

	//START: add Frame and lenses if both only frame and only lenses are enabled
	const frameAndLens = benefits.find(el => el.serviceId === 4);
	if (frameAndLens && frameAndLens.memberBenefitAmount === null) {
		if (
			benefits.find(el => el.serviceId === 1 && el.memberBenefitAmount !== null) &&
			benefits.find(el => el.serviceId === 2 && el.memberBenefitAmount !== null)
		) {
			benefits = [
				...benefits.filter(el => el.serviceId !== 4),
				{ ...frameAndLens, eligible: 1, memberBenefitAmount: 0 },
			];
		}
	}
	//END: add Frame and lenses if both only frame and only lenses are enabled

	const exam = getOptions(benefits, optionsExam);
	const materials = getOptions(benefits, optionsMaterials);
	const lensOptions = getOptions(benefits, optionsSpecificLens);

	const onChange: (
		event: ChangeEvent<HTMLInputElement>,
		value: string,
	) => void = (event, value) => {
		const newComponents = refreshComponents({
			exam: formData.exam,
			materials: formData.materials,
			lensType: formData.lensType,
			detailLenses: !!lensBenefits.find(el => el.memberBenefitAmount),
			[event.target.name]: Number(value),
		});

		let lensTypeReset = {};
		if (
			event.target.name === 'materials' &&
			!lensTypeRequired.includes(Number(value))
		) {
			lensTypeReset = { lensType: undefined };
		}

		let prescriptionReset = {};
		if (event.currentTarget.value === '5')
			//contacts have less fields
			prescriptionReset = {
				newPrescriptionContent: undefined,
			};

		setFormData((prev: IClaim) => ({
			...prev,
			...lensTypeReset,
			...prescriptionReset,
			[event.target.name]: Number(value),
			amount: { ...prev.amount, components: newComponents },
		}));

		if (event.currentTarget.value === '5')
			//contacts have less fields
			dispatch(getPrescriptions({}));
	};

	useEffect(() => {
		//this serves to preselect stuff if user has only one option
		if (!formData.beneficiarySubscriptionUuid) {
			return;
		}
		if (exam.length === 1 || materials.length === 1) {
			//check if it would change anything
			if (
				formData.exam === (exam.length === 1 ? exam[0].value : formData.exam) &&
				formData.materials ===
					(materials.length === 1 ? materials[0].value : formData.materials) &&
				formData.lensType ===
					(lensOptions.length === 1 ? lensOptions[0].value : formData.lensType)
			) {
				return;
			}

			const newComponents = refreshComponents({
				exam: exam.length === 1 ? exam[0].value : formData.exam,
				materials: materials.length === 1 ? materials[0].value : formData.materials,
				lensType:
					lensOptions.length === 1 ? lensOptions[0].value : formData.lensType,
				detailLenses: !!lensBenefits.find(el => el.memberBenefitAmount),
			});

			setFormData((prev: IClaim) => ({
				...prev,
				exam: exam.length === 1 ? exam[0].value : prev.exam,
				materials: materials.length === 1 ? materials[0].value : prev.materials,
				amount: { ...prev.amount, components: newComponents },
			}));
		}
		// eslint-disable-next-line
	}, [exam.length, materials.length, formData.beneficiarySubscriptionUuid]);

	const dialogConfirm = useDialogConfirm();

	return (
		<>
			<Stack gap={'20px'}>
				<Typography variant="title6">
					{tClientLabels('newClaim.selectedPage.sectionTitile.selectType')}
				</Typography>
				{exam.length > 1 || (exam.length === 1 && exam[0].value > 0) ? (
					<StackRadio
						name="exam"
						onChange={onChange}
						value={formData.exam}
						label={tClientLabels('newClaim.exam.title')}
						options={exam}
					/>
				) : null}
				{materials.length > 1 ||
				(materials.length === 1 && materials[0].value > 0) ? (
					<StackRadio
						name="materials"
						onChange={onChange}
						value={formData.materials}
						label={tClientLabels('newClaim.materials.title')}
						options={materials}
					/>
				) : null}
				{isLensTypeRequired ? (
					<StackRadio
						name="lensType"
						onChange={onChange}
						value={formData.lensType ?? 0}
						label={tClientLabels('newClaim.lensType.title')}
						options={lensOptions.map(el => ({ ...el, value: el.statisticValue! }))}
					/>
				) : null}
			</Stack>
			<Box alignItems="start" sx={{ mt: '30px' }}>
				<Button
					disabled={
						!(
							formData.exam &&
							formData.materials &&
							(!isLensTypeRequired || formData.lensType)
						)
					}
					variant="contained"
					sx={{ mt: '10px' }}
					onClick={() => {
						if (formData.exam === -1 && formData.materials === -2) {
							dialogConfirm.open(NO_MAT_NO_EXAM);
						} else {
							submitPage!();
						}
					}}
				>
					{tClientLabels('newClaim.selectedPage.proceed')}
				</Button>
			</Box>
		</>
	);
};

const refreshComponents = (inputs: {
	exam: number;
	materials: number;
	lensType?: number;
	detailLenses: boolean;
}) => {
	const { exam, materials, lensType, detailLenses } = inputs;

	const newComponents: IClaimComponents[] = [];

	const addOrRemoveComponent = (serviceId: number) => {
		newComponents.push({
			grossRetail: 0,
			retailAmount: 0,
			retailNetOfTax: 0,
			tax: 0,
			total: 0,
			contractualAdjustmentAmount: 0,
			memberPayAmount: 0,
			planPayAmount: 0,
			serviceId: serviceId,
			discountedAmount: 0,
		});
	};

	switch (exam) {
		case 3:
			addOrRemoveComponent(3);
			break;
		case 20:
			addOrRemoveComponent(20);
			break;
	}

	switch (materials) {
		case 1:
			if (!detailLenses) addOrRemoveComponent(1);
			break;
		case 2:
			addOrRemoveComponent(2);
			break;
		case 4:
			addOrRemoveComponent(2);
			if (!detailLenses) addOrRemoveComponent(1);
			break;
		case 5:
			addOrRemoveComponent(5);
			break;
	}

	if (detailLenses)
		switch (lensType) {
			case 1:
				addOrRemoveComponent(12);
				break;
			case 2:
				addOrRemoveComponent(13);
				break;
			case 3:
				addOrRemoveComponent(14);
				break;
			case 4:
				addOrRemoveComponent(15);
				break;
			default:
				break;
		}

	return newComponents;
};

export default Page0;
